import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, 
  Building2, 
  FileCheck, 
  Shield, 
  Receipt, 
  Calculator, 
  FileText, 
  Scale, 
  Globe,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle2,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import StickyHeader from '../components/StickyHeader';
import Footer from '../components/Footer';
import { openWhatsApp } from '../utils/whatsappUtils';

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const categoryRefs = useRef({});
  
  // Get category from URL params or hash
  const urlCategory = searchParams.get('category') || location.hash.replace('#', '');
  const [expandedCategory, setExpandedCategory] = useState(urlCategory || null);
  const [highlightedCategory, setHighlightedCategory] = useState(urlCategory || null);

  const handleGetStarted = () => {
    openWhatsApp('homepage');
  };

  const handleServiceClick = (serviceSlug) => {
    // Navigate to service detail page
    navigate(`/service/${serviceSlug}`);
  };

  // Auto-expand and scroll to category from URL
  useEffect(() => {
    if (urlCategory && categoryRefs.current[urlCategory]) {
      setExpandedCategory(urlCategory);
      setHighlightedCategory(urlCategory);
      
      // Scroll to category after a short delay to ensure render
      setTimeout(() => {
        categoryRefs.current[urlCategory]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
      
      // Remove highlight after 3 seconds
      setTimeout(() => {
        setHighlightedCategory(null);
      }, 3000);
    }
  }, [urlCategory]);

  const categories = [
    {
      id: 'business-setup',
      title: 'Business Setup',
      icon: Building2,
      description: 'Company formation and business registration',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      services: [
        { name: 'Proprietorship', slug: 'proprietorship', price: 'From ₹1,499' },
        { name: 'Partnership', slug: 'partnership', price: 'From ₹3,999' },
        { name: 'One Person Company (OPC)', slug: 'opc', price: 'From ₹7,499' },
        { name: 'Limited Liability Partnership (LLP)', slug: 'llp', price: 'From ₹6,999' },
        { name: 'Private Limited Company', slug: 'private-limited', price: 'From ₹7,999' },
        { name: 'Section 8 Company', slug: 'section-8', price: 'From ₹14,999' },
        { name: 'Trust Registration', slug: 'trust', price: 'From ₹9,999' },
        { name: 'Public Limited Company', slug: 'public-limited', price: 'From ₹24,999' },
        { name: 'Producer Company', slug: 'producer-company', price: 'From ₹24,999' },
        { name: 'Indian Subsidiary', slug: 'indian-subsidiary', price: 'From ₹19,999' },
      ],
    },
    {
      id: 'licenses',
      title: 'Licenses',
      icon: FileCheck,
      description: 'Business licenses and regulatory registrations',
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      services: [
        { name: 'Startup India', slug: 'startup-india', price: 'From ₹2,999' },
        { name: 'Trade License', slug: 'trade-license', price: 'From ₹4,999' },
        { name: 'FSSAI Registration', slug: 'fssai-registration', price: 'From ₹2,499' },
        { name: 'FSSAI License', slug: 'fssai-license', price: 'From ₹4,999' },
        { name: 'FSSAI Renewal', slug: 'fssai-renewal', price: 'From ₹2,499' },
        { name: 'Halal License & Certification', slug: 'halal-license', price: 'Price varies' },
        { name: 'ICEGATE Registration', slug: 'icegate', price: 'From ₹2,999' },
        { name: 'Import Export Code (IEC)', slug: 'iec', price: 'From ₹2,999' },
        { name: 'Legal Entity Identifier (LEI)', slug: 'lei', price: 'From ₹4,999' },
        { name: 'ISO Registration', slug: 'iso', price: 'From ₹9,999' },
        { name: 'PF Registration', slug: 'pf-registration', price: 'From ₹2,999' },
        { name: 'ESI Registration', slug: 'esi-registration', price: 'From ₹2,999' },
        { name: 'Professional Tax Registration', slug: 'professional-tax', price: 'From ₹1,999' },
        { name: 'RCMC Registration', slug: 'rcmc', price: 'From ₹4,999' },
        { name: '12A Registration', slug: '12a-registration', price: 'From ₹5,999' },
        { name: '80G Registration', slug: '80g-registration', price: 'From ₹5,999' },
        { name: '12A & 80G Registration', slug: '12a-80g', price: 'From ₹9,999' },
        { name: 'Barcode Registration', slug: 'barcode', price: 'From ₹4,999' },
        { name: 'BIS Registration', slug: 'bis', price: 'From ₹14,999' },
        { name: 'Certificate of Incumbency', slug: 'incumbency', price: 'From ₹4,999' },
        { name: 'Darpan Registration', slug: 'darpan', price: 'From ₹1,999' },
        { name: 'Digital Signature (DSC)', slug: 'dsc', price: 'From ₹999' },
        { name: 'Shop Act Registration', slug: 'shop-act', price: 'From ₹2,499' },
        { name: 'MSME / Udyam Registration', slug: 'msme', price: 'From ₹999' },
        { name: 'Fire License', slug: 'fire-license', price: 'From ₹4,999' },
      ],
    },
    {
      id: 'trademark',
      title: 'Trademark',
      icon: Shield,
      description: 'Intellectual property protection and registration',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      services: [
        { name: 'Trademark Registration', slug: 'trademark-registration', price: 'From ₹1,499' },
        { name: 'Trademark Objection / Reply', slug: 'trademark-objection', price: 'From ₹2,999' },
        { name: 'Trademark Opposition', slug: 'trademark-opposition', price: 'From ₹4,999' },
        { name: 'Trademark Hearing', slug: 'trademark-hearing', price: 'From ₹4,999' },
        { name: 'Trademark Rectification', slug: 'trademark-rectification', price: 'From ₹2,999' },
        { name: 'Trademark Renewal', slug: 'trademark-renewal', price: 'From ₹2,499' },
        { name: 'Trademark Transfer', slug: 'trademark-transfer', price: 'From ₹4,999' },
        { name: 'Trademark Certificate', slug: 'trademark-certificate', price: 'From ₹999' },
        { name: 'TM Infringement Notice', slug: 'tm-infringement', price: 'From ₹2,999' },
        { name: 'Expedited Trademark Registration', slug: 'expedited-trademark', price: 'From ₹4,999' },
        { name: 'Trademark Protection', slug: 'trademark-protection', price: 'From ₹1,999' },
        { name: 'Copyright Registration', slug: 'copyright-registration', price: 'From ₹4,999' },
        { name: 'Copyright Objection', slug: 'copyright-objection', price: 'From ₹2,999' },
        { name: 'Design Registration', slug: 'design-registration', price: 'From ₹7,999' },
        { name: 'Design Objection', slug: 'design-objection', price: 'From ₹2,999' },
        { name: 'Patent Registration', slug: 'patent-registration', price: 'From ₹24,999' },
        { name: 'Logo Designing', slug: 'logo-designing', price: 'From ₹2,999' },
      ],
    },
    {
      id: 'gst',
      title: 'GST',
      icon: Receipt,
      description: 'GST registration, filing, and compliance',
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      services: [
        { name: 'GST Registration', slug: 'gst-registration', price: 'From ₹1,499' },
        { name: 'GST Registration for Foreigners', slug: 'gst-foreigners', price: 'From ₹4,999' },
        { name: 'Monthly GST Filing', slug: 'gst-return', price: 'From ₹999/month' },
        { name: 'Quarterly GST Filing', slug: 'gst-return-quarterly', price: 'From ₹2,499/quarter' },
        { name: 'GST LUT Filing', slug: 'gst-lut', price: 'From ₹999' },
        { name: 'GST Modification', slug: 'gst-modification', price: 'From ₹999' },
        { name: 'GST Cancellation / Revocation', slug: 'gst-cancellation', price: 'From ₹999' },
        { name: 'GST Notice Reply', slug: 'gst-notice', price: 'From ₹2,999' },
        { name: 'GSTR-9 (Annual Return)', slug: 'gstr-9', price: 'From ₹4,999' },
        { name: 'GSTR-10 (Final Return)', slug: 'gstr-10', price: 'From ₹1,999' },
        { name: 'Virtual Office + GSTIN', slug: 'virtual-office-gst', price: 'From ₹9,999/year' },
      ],
    },
    {
      id: 'tax-payroll',
      title: 'Tax & Payroll',
      icon: Calculator,
      description: 'Tax filing, returns, and payroll compliance',
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
      services: [
        { name: 'Individual ITR Filing', slug: 'itr-efiling', price: 'From ₹999' },
        { name: 'Business ITR Filing', slug: 'business-tax', price: 'From ₹2,499' },
        { name: 'Partnership / LLP ITR', slug: 'llp-itr', price: 'From ₹2,999' },
        { name: 'Company ITR Filing', slug: 'company-itr', price: 'From ₹4,999' },
        { name: 'Trust / NGO ITR Filing', slug: 'trust-itr', price: 'From ₹5,999' },
        { name: 'Revised Return (ITR-U)', slug: 'revised-itr', price: 'From ₹2,499' },
        { name: 'Income Tax Notice Reply', slug: 'it-notice', price: 'From ₹2,999' },
        { name: 'TDS Return Filing', slug: 'tds-return', price: 'From ₹1,499/return' },
        { name: 'TAN Registration', slug: 'tan-registration', price: 'From ₹999' },
        { name: '15CA / 15CB Filing', slug: '15ca-15cb', price: 'From ₹3,999' },
        { name: 'PF (EPF) Registration', slug: 'pf-registration', price: 'From ₹2,999' },
        { name: 'ESIC Registration', slug: 'esi-registration', price: 'From ₹2,999' },
        { name: 'PF / ESIC Return Filing', slug: 'pf-esi-return', price: 'From ₹999/month' },
      ],
    },
    {
      id: 'mca',
      title: 'MCA',
      icon: FileText,
      description: 'Company law compliance and filings',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      services: [
        { name: 'Annual Company Compliance', slug: 'company-compliance', price: 'From ₹5,999/year' },
        { name: 'LLP Annual Compliance', slug: 'llp-compliance', price: 'From ₹4,999/year' },
        { name: 'OPC Annual Compliance', slug: 'opc-compliance', price: 'From ₹5,999/year' },
        { name: 'Company Name Change', slug: 'name-change', price: 'From ₹6,999' },
        { name: 'Registered Office Change', slug: 'office-change', price: 'From ₹2,999' },
        { name: 'Director Change', slug: 'director-change', price: 'From ₹2,999' },
        { name: 'Remove Director', slug: 'remove-director', price: 'From ₹2,999' },
        { name: 'DIN KYC', slug: 'din-ekyc', price: 'From ₹999' },
        { name: 'DIN Reactivation', slug: 'din-reactivation', price: 'From ₹1,999' },
        { name: 'ADT-1 Filing', slug: 'adt-1', price: 'From ₹1,999' },
        { name: 'DPT-3 Filing', slug: 'dpt-3', price: 'From ₹1,999' },
        { name: 'LLP Form 11 Filing', slug: 'llp-form-11', price: 'From ₹999' },
        { name: 'Dormant Status Filing', slug: 'dormant-status', price: 'From ₹4,999' },
        { name: 'MOA / AOA Amendment', slug: 'moa-amendment', price: 'From ₹3,999' },
        { name: 'Authorized Capital Increase', slug: 'capital-increase', price: 'From ₹3,999' },
        { name: 'Share Transfer', slug: 'share-transfer', price: 'From ₹4,999' },
        { name: 'Demat of Shares', slug: 'demat-shares', price: 'From ₹7,999' },
        { name: 'Commencement (INC-20A)', slug: 'inc-20a', price: 'From ₹1,999' },
        { name: 'Winding Up – LLP', slug: 'llp-winding', price: 'From ₹14,999' },
        { name: 'Winding Up – Company', slug: 'company-winding', price: 'From ₹24,999' },
      ],
    },
    {
      id: 'compliance',
      title: 'Compliance & Advisory',
      icon: Scale,
      description: 'Ongoing compliance, filings, and advisory services',
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      services: [
        { name: 'FDI Filing with RBI', slug: 'fdi-filing', price: 'From ₹9,999' },
        { name: 'FLA Return Filing', slug: 'fla-return', price: 'From ₹4,999' },
        { name: 'FSSAI Return Filing', slug: 'fssai-return', price: 'From ₹999' },
        { name: 'PF Return Filing', slug: 'pf-return', price: 'From ₹999/return' },
        { name: 'ESI Return Filing', slug: 'esi-return', price: 'From ₹999/return' },
        { name: 'Professional Tax Filing', slug: 'pt-return', price: 'From ₹999' },
        { name: 'Proprietorship Compliance', slug: 'proprietorship-compliance', price: 'From ₹2,999/year' },
        { name: 'Partnership Compliance', slug: 'partnership-compliance', price: 'From ₹4,999/year' },
        { name: 'Bookkeeping', slug: 'bookkeeping', price: 'From ₹2,999/month' },
        { name: 'Payroll & HR', slug: 'hr-payroll', price: 'From ₹1,999/month' },
        { name: 'Business Plan', slug: 'business-plan', price: 'From ₹9,999' },
        { name: 'CA Support', slug: 'ca-support', price: 'Price varies' },
      ],
    },
    {
      id: 'global',
      title: 'Global',
      icon: Globe,
      description: 'International company formation and trademark',
      iconBg: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      services: [
        { name: 'USA Company Incorporation', slug: 'usa-company', price: 'From ₹19,999' },
        { name: 'UK Company Incorporation', slug: 'uk-company', price: 'From ₹14,999' },
        { name: 'UAE Company Setup', slug: 'uae-company', price: 'From ₹49,999' },
        { name: 'Singapore Company', slug: 'singapore-company', price: 'From ₹39,999' },
        { name: 'International Trademark', slug: 'international-trademark', price: 'From ₹9,999' },
        { name: 'USA Trademark Registration', slug: 'usa-trademark', price: 'From ₹14,999' },
        { name: 'UAE eCommerce License', slug: 'uae-ecommerce', price: 'From ₹39,999' },
        { name: 'Embassy Attestation', slug: 'uae-attestation', price: 'From ₹4,999' },
        { name: 'Ajman Free Zone', slug: 'ajman-freezone', price: 'From ₹49,999' },
      ],
    },
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <StickyHeader />

      {/* ═══════════════════════════════════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50/50 overflow-hidden" data-testid="pricing-hero">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center relative">
          {/* Subtle background glow */}
          <div className="absolute inset-0 bg-gradient-to-tr from-orange-100/30 via-blue-100/20 to-violet-100/30 blur-3xl -z-10 animate-fade-in" />
          
          {/* Trust badges */}
          <div className="flex flex-wrap gap-2 justify-center mb-6 animate-fade-in-up">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 text-xs font-medium rounded-full border border-orange-200">
              <Receipt className="h-3.5 w-3.5" />
              Transparent pricing
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full border border-emerald-200">
              <CheckCircle2 className="h-3.5 w-3.5" />
              No hidden fees
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6 text-slate-900 animate-fade-in-up animation-delay-100">
            Browse our services
            <span className="block bg-gradient-to-r from-orange-600 via-blue-600 to-violet-600 bg-clip-text text-transparent">
              clear scope, clear pricing
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed mb-8 animate-fade-in-up animation-delay-200">
            Every compliance service is priced by context and complexity. No bundles, no confusion.
          </p>
          
          <div className="flex justify-center animate-fade-in-up animation-delay-300">
            <Button 
              size="lg" 
              onClick={handleGetStarted} 
              className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 text-base rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl transition-all"
              data-testid="hero-cta"
            >
              Describe your situation
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>

          <p className="text-sm text-slate-500 mt-6 animate-fade-in-up animation-delay-400">
            Government fees at actuals • Expert review included • No surprises
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          SERVICE CATALOGUE
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-slate-50/60" data-testid="service-catalogue">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">
              Service catalogue
            </p>
            <h2 className="text-2xl md:text-3xl font-light text-slate-900 mb-3">
              Find the right service for your needs
            </h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">
              Select a category to explore services, or describe your situation for a custom recommendation.
            </p>
          </div>

          {/* Category Cards - Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {categories.map((category) => {
              const Icon = category.icon;
              const isHighlighted = highlightedCategory === category.id;
              const isExpanded = expandedCategory === category.id;
              return (
                <button
                  key={category.id}
                  ref={el => categoryRefs.current[category.id] = el}
                  onClick={() => toggleCategory(category.id)}
                  className={`text-left p-5 rounded-xl border transition-all duration-200 ${
                    isExpanded
                      ? 'bg-white border-slate-900 shadow-md'
                      : isHighlighted
                      ? 'bg-white border-slate-900 shadow-lg ring-2 ring-slate-900 ring-offset-2'
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  <div className={`h-10 w-10 ${category.iconBg} rounded-lg flex items-center justify-center mb-3`}>
                    <Icon className={`h-5 w-5 ${category.iconColor}`} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{category.title}</h3>
                  <p className="text-xs text-slate-500 line-clamp-2">{category.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-slate-400">{category.services.length} services</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Mobile Accordion */}
          <div className="md:hidden space-y-3 mb-8">
            {categories.map((category) => {
              const Icon = category.icon;
              const isExpanded = expandedCategory === category.id;
              return (
                <div key={category.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="w-full flex items-center justify-between p-4"
                    data-testid={`category-mobile-${category.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 ${category.iconBg} rounded-lg flex items-center justify-center`}>
                        <Icon className={`h-4 w-4 ${category.iconColor}`} />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-slate-900 text-sm">{category.title}</h3>
                        <p className="text-xs text-slate-400">{category.services.length} services</p>
                      </div>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isExpanded && (
                    <div className="border-t border-slate-100 p-4 pt-2">
                      <div className="space-y-2">
                        {category.services.map((service, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleServiceClick(service.slug)}
                            className="w-full flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <span className="text-sm text-slate-700">{service.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-400">{service.price}</span>
                              <ChevronRight className="h-4 w-4 text-slate-300" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Expanded Category Services - Desktop */}
          {expandedCategory && (
            <div className="hidden md:block" data-testid={`services-${expandedCategory}`}>
              {categories.filter(c => c.id === expandedCategory).map((category) => (
                <Card key={category.id} className="border border-slate-200 bg-white shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                      <div className={`h-10 w-10 ${category.iconBg} rounded-lg flex items-center justify-center`}>
                        <category.icon className={`h-5 w-5 ${category.iconColor}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{category.title}</h3>
                        <p className="text-sm text-slate-500">{category.description}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {category.services.map((service, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleServiceClick(service.slug)}
                          className="group flex flex-col items-start p-4 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50/50 transition-all duration-200 text-left"
                          data-testid={`service-${service.slug}`}
                        >
                          <span className="text-sm font-medium text-slate-800 group-hover:text-slate-900 mb-1">
                            {service.name}
                          </span>
                          <span className="text-xs text-slate-400">{service.price}</span>
                          <div className="flex items-center gap-1 mt-2 text-xs text-slate-400 group-hover:text-slate-600">
                            <span>View details</span>
                            <ChevronRight className="h-3 w-3" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Empty State - No category selected */}
          {!expandedCategory && (
            <div className="hidden md:block text-center py-12 bg-white rounded-xl border border-dashed border-slate-200">
              <p className="text-slate-400 mb-2">Select a category above to browse services</p>
              <p className="text-sm text-slate-300">Or describe your situation for personalized recommendations</p>
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          PRICING PHILOSOPHY
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20" data-testid="pricing-philosophy">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">
              Our pricing approach
            </p>
            <h2 className="text-2xl md:text-3xl font-light text-slate-900 mb-3">
              Transparent, modular pricing
            </h2>
            <p className="text-base text-slate-500 max-w-xl mx-auto">
              Every service is priced individually with clear scope. No hidden charges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
              <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm">
                <Receipt className="h-5 w-5 text-slate-600" />
              </div>
              <h3 className="font-medium text-slate-900 mb-2">Government fees at actuals</h3>
              <p className="text-sm text-slate-500">Statutory fees are billed separately at exact amounts charged by authorities.</p>
            </div>

            <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
              <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm">
                <CheckCircle2 className="h-5 w-5 text-slate-600" />
              </div>
              <h3 className="font-medium text-slate-900 mb-2">Expert review included</h3>
              <p className="text-sm text-slate-500">Where stated, CA/CS/legal review is part of the service — not an upsell.</p>
            </div>

            <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
              <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm">
                <Clock className="h-5 w-5 text-slate-600" />
              </div>
              <h3 className="font-medium text-slate-900 mb-2">Add-ons for urgency</h3>
              <p className="text-sm text-slate-500">Expedited timelines and priority handling available as optional add-ons.</p>
            </div>

            <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
              <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm">
                <Shield className="h-5 w-5 text-slate-600" />
              </div>
              <h3 className="font-medium text-slate-900 mb-2">No hidden cross-sells</h3>
              <p className="text-sm text-slate-500">What you see is what you pay. No surprise charges after you start.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FINAL CTA
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-slate-900 text-white" data-testid="pricing-cta-section">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-light mb-5">
            Not sure what you need?
          </h2>
          <p className="text-lg text-slate-300 mb-10 leading-relaxed max-w-xl mx-auto">
            Describe your situation and we'll recommend the right services with exact pricing and timelines.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-slate-900 hover:bg-slate-100 px-8" 
            onClick={handleGetStarted}
            data-testid="final-cta-button"
          >
            Describe your situation <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          TRUST ANCHOR
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-8 bg-slate-50 border-t border-slate-100" data-testid="trust-anchor">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-slate-400" />
              <span>Reviewed by qualified CA, CS & legal professionals</span>
            </div>
            <span className="hidden sm:inline text-slate-300">·</span>
            <span>Built for Indian compliance, end-to-end</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
